import { Button, Card } from 'antd';
import React, { useState } from 'react';

import { SplitDescriptions } from '../../../components/SplitDescriptions';
import { apiClient } from '../../../graphql';
import {
  EmergencyScenarioMonitoringFragment,
  EmergencyScenarioStatus,
  EmergencyScenarioSyncStatus,
  EmergencyScenarioTableFragment,
  useemergencyScenarioMonitoringSubscription,
  usegetEmergencyScenariosQuery,
} from '../../../graphql/api/generated';
import createDetailsLayout from '../../../layouts/createDetailsLayout';
import { createdAtAndByColumn, updatedAtAndByColumn } from '../../common/columns';
import * as columns from '../columns';
import { EmergencyScenarioForm } from '../components/EmergencyScenarioForm';
import { EmergencyScenarioMonitoring } from '../components/EmergencyScenarioMonitoring';
import { EmergencyScenarioTabs } from '../components/EmergencyScenarioTabs';
import model from '../model';

type EmergencyScenarioWithMonitoringData = EmergencyScenarioTableFragment & {
  emergencyScenarioMonitoring?: EmergencyScenarioMonitoringFragment | null;
};

const DetailsLayout = createDetailsLayout<EmergencyScenarioWithMonitoringData>();

export const EmergencyScenarioDetailsPage: React.FC<any> = () => {
  const [editMode, setEditMode] = useState(false);

  return (
    <DetailsLayout
      modelName='EmergencyScenario'
      useQuery={(emergencyScenarioId) => {
        const { data: subscriptionData } = useemergencyScenarioMonitoringSubscription({
          variables: {
            emergencyScenarioId,
          },
          client: apiClient,
        });

        const { data: queryData, ...queryResult } = usegetEmergencyScenariosQuery({
          variables: {
            input: {
              filters: { emergencyScenarioId },
            },
          },
          client: apiClient,
        });

        const queriedEmergencyScenario = queryData?.emergencyScenarios.list[0];
        const emergencyScenarioMonitoring = subscriptionData?.emergencyScenarioMonitoring;
        const subscribedEmergencyScenario = emergencyScenarioMonitoring?.emergencyScenario;

        // The queried emergency scenario provides more immediate feedback for users
        // updating the status or managing AP emergency plans (automatically updated cache).
        let emergencyScenario = queriedEmergencyScenario;
        const queriedUpdatedAt = queriedEmergencyScenario?.updatedAt;
        const subscribedUpdatedAt = subscribedEmergencyScenario?.updatedAt;

        // But we will overwrite the queried data with the more recent subscription data
        // to update the status for other users who might be viewing/monitoring the page.
        if (subscribedUpdatedAt && queriedUpdatedAt && subscribedUpdatedAt > queriedUpdatedAt) {
          emergencyScenario = {
            ...queriedEmergencyScenario,
            ...subscribedEmergencyScenario,
          };
        }

        return {
          ...queryResult,
          data: queriedEmergencyScenario ? {
            // Merge monitoring data
            emergencyScenario: { ...emergencyScenario, emergencyScenarioMonitoring },
          } : undefined,
        };
      }}
      body={(emergencyScenario) => {
        const canEdit = model.permissions.canUpdateRow(emergencyScenario);
        const { createdAt, updatedAt } = emergencyScenario;

        if (canEdit && editMode) {
          return (
            <EmergencyScenarioForm
              isNew={false}
              emergencyScenario={emergencyScenario}
              onCancel={() => setEditMode(false)}
              onSubmit={() => setEditMode(false)}
            />
          );
        }

        return (
          <>
            <SplitDescriptions
              row={emergencyScenario}
              leftSideColumns={[
                columns.propertyColumn,
                columns.nameColumn,
                columns.statusColumn,
              ]}
              rightSideColumns={[
                columns.accessPointsColumn,
                createdAtAndByColumn,
                updatedAt !== createdAt ? updatedAtAndByColumn : null,
              ]}
            />
            {canEdit && (
              <Button
                onClick={() => setEditMode(true)}
                style={{ float: 'left', clear: 'both', marginTop: 15 }}
              >
                Edit
              </Button>
            )}
          </>
        );
      }}
      footer={(emergencyScenario) => {
        const { emergencyScenarioMonitoring } = emergencyScenario;
        const queriedStatus = emergencyScenario.status;
        const subscribedStatus = emergencyScenarioMonitoring?.emergencyScenario?.status;

        if (queriedStatus !== subscribedStatus) {
          // Wait for monitoring subscription to either:
          // 1) initially load OR 2) update after toggling status
          return <Card loading bordered={false} />;
        }

        if (emergencyScenarioMonitoring) {
          const { syncStatus } = emergencyScenarioMonitoring;

          if (
            // Always show monitoring when emergency scenario is activated
            subscribedStatus === EmergencyScenarioStatus.ACTIVE ||
            subscribedStatus === EmergencyScenarioStatus.TESTING ||
            // Keep monitoring visible during deactivation if it is not successful
            syncStatus === EmergencyScenarioSyncStatus.WAITING ||
            syncStatus === EmergencyScenarioSyncStatus.ERROR
          ) {
            return <EmergencyScenarioMonitoring emergencyScenarioMonitoring={emergencyScenarioMonitoring} />;
          }
        }

        return <EmergencyScenarioTabs emergencyScenario={emergencyScenario} />;
      }}
    />
  );
};
